import { useMsal, useIsAuthenticated } from '@azure/msal-react';
import type { SilentRequest } from '@azure/msal-browser';
import { InteractionRequiredAuthError } from '@azure/msal-browser';
import { useCallback } from 'react';
import { getMsalConfigurationError, loginRequest, trapiTokenRequest } from '../config/msal';
import { AuthRequiredError } from '../utils/errors';

// Re-export for backward compatibility
export { AuthRequiredError } from '../utils/errors';

// ---------------------------------------------------------------------------
// Module-level token cache and inflight deduplication.
// ---------------------------------------------------------------------------
let _tokenCache: { token: string; expiresOn: number; accountId: string } | null = null;
let _inflightPromise: Promise<string> | null = null;

function isSilentTokenTimeout(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;

  const maybeError = error as { errorCode?: string };
  return maybeError.errorCode === 'timed_out' ||
    maybeError.errorCode === 'monitor_window_timeout';
}

export function useMsalAuth() {
  const { instance, accounts } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const primaryAccount = accounts[0] ?? instance.getActiveAccount() ?? null;

  const login = useCallback(async () => {
    const msalConfigurationError = getMsalConfigurationError();
    if (msalConfigurationError) {
      throw new AuthRequiredError(msalConfigurationError);
    }

    await instance.loginRedirect(loginRequest);
  }, [instance]);

  const logout = useCallback(async () => {
    _tokenCache = null;
    await instance.logoutRedirect({
      postLogoutRedirectUri: window.location.origin,
    });
  }, [instance]);

  /**
   * Acquire a token following the Microsoft-recommended pattern:
   *   acquireTokenSilent  ->  acquireTokenRedirect
   *
   * If the refresh token is still valid, the user never sees anything.
   * If interaction is required (expired session, password change, etc.),
   * the browser redirects to Entra ID and comes back with fresh tokens.
   * No popups, no banners, no race conditions.
   */
  const getToken = useCallback(async (): Promise<string> => {
    const msalConfigurationError = getMsalConfigurationError();
    if (msalConfigurationError) {
      throw new AuthRequiredError(msalConfigurationError);
    }

    const currentAccountId = primaryAccount?.homeAccountId ?? '';

    // Return cached token if still valid (60s buffer)
    const now = Date.now();
    if (
      _tokenCache &&
      _tokenCache.expiresOn > now + 60_000 &&
      _tokenCache.accountId === currentAccountId
    ) {
      return _tokenCache.token;
    }

    // Deduplicate concurrent calls
    if (_inflightPromise) return _inflightPromise;

    const account = primaryAccount ?? instance.getActiveAccount() ?? instance.getAllAccounts()[0];
    if (!account) {
      // No account — MsalAuthenticationTemplate handles the redirect
      return '';
    }

    const promise = (async () => {
      try {
        const silentReq: SilentRequest = { ...trapiTokenRequest, account };
        const result = await instance.acquireTokenSilent(silentReq);
        _tokenCache = {
          token: result.accessToken,
          expiresOn: result.expiresOn?.getTime() ?? Date.now() + 3_600_000,
          accountId: account.homeAccountId ?? '',
        };
        return result.accessToken;
      } catch (error) {
        if (error instanceof InteractionRequiredAuthError || isSilentTokenTimeout(error)) {
          // If silent iframe auth times out, fall back to the top-level redirect flow.
          await instance.acquireTokenRedirect({ ...trapiTokenRequest, account });
          return ''; // never reached
        }
        throw error;
      } finally {
        _inflightPromise = null;
      }
    })();

    _inflightPromise = promise;
    return promise;
  }, [instance, primaryAccount]);

  const getUserOid = useCallback(async (): Promise<string> => {
    const account = primaryAccount ?? instance.getActiveAccount() ?? instance.getAllAccounts()[0];
    if (!account) return '';
    return account.localAccountId || account.homeAccountId || '';
  }, [instance, primaryAccount]);

  return {
    account: primaryAccount,
    isAuthenticated,
    login,
    logout,
    getToken,
    getUserOid,
  };
}
